import { useRef, useState } from "react";
import { FileUp, Loader2, ScanLine, Target, X } from "lucide-react";
import { ACCEPT, MAX_FILE } from "@/lib/resumeScan";
import { fitScan } from "@/lib/fitScan";

export const FitForm = ({ onDone }) => {
  const [mode, setMode] = useState("upload");
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const input = useRef(null);
  const ready = role.trim().length >= 2 && (mode === "upload" ? !!file : text.trim().length >= 200);

  const pick = (f) => { setErr(""); if (!f) return; if (f.size > MAX_FILE) { setFile(null); setErr("Please choose a file under 5 MB."); return; } setFile(f); };
  const submit = async () => {
    setBusy(true); setErr("");
    try { onDone(await fitScan({ ...(mode === "upload" ? { file } : { text: text.trim() }), target_role: role.trim() })); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="fit-form">
      <label className="block">
        <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37]"><Target className="h-3.5 w-3.5" />Your target role</span>
        <input value={role} onChange={(e) => setRole(e.target.value.slice(0, 80))} placeholder="e.g. Senior Product Manager, Remote" data-testid="fit-role-input"
          className="w-full rounded-xl border border-[#27272A] bg-[#09090B] px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#D4AF37]/60 focus:outline-none" />
      </label>
      <div className="inline-flex rounded-full border border-[#27272A] bg-[#09090B] p-1 text-xs">
        {[["upload", "Upload file"], ["paste", "Paste text"]].map(([m, l]) => (
          <button key={m} onClick={() => { setMode(m); setErr(""); }} data-testid={`fit-mode-${m}`}
            className={`rounded-full px-3.5 py-1.5 transition-colors ${mode === m ? "bg-[#D4AF37] font-semibold text-black" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
        ))}
      </div>
      {mode === "upload" ? (
        <div onClick={() => input.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#D4AF37]/40 bg-[#09090B]/60 p-8 text-center transition-colors hover:border-[#D4AF37]/70" data-testid="fit-dropzone">
          <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="fit-file-input" />
          {file ? (
            <span className="flex items-center gap-2 text-sm text-zinc-200" data-testid="fit-file-name">{file.name}
              <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-zinc-500 hover:text-red-400" data-testid="fit-file-remove"><X className="h-4 w-4" /></button>
            </span>
          ) : <><FileUp className="h-7 w-7 text-[#D4AF37]" /><span className="text-sm text-zinc-300">Drop your resume or tap to choose</span><span className="text-[11px] text-zinc-500">PDF, DOCX, DOC or TXT · up to 5 MB</span></>}
        </div>
      ) : (
        <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 15000))} rows={7} placeholder="Paste your full resume text here…" data-testid="fit-text-input"
          className="w-full resize-none rounded-xl border border-[#27272A] bg-[#09090B] p-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#D4AF37]/60 focus:outline-none" />
      )}
      <button onClick={submit} disabled={!ready || busy} data-testid="fit-submit-button"
        className="lp-shine relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-[#D4AF37] via-[#F3C649] to-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-black shadow-[0_0_30px_rgba(212,175,55,0.3)] disabled:opacity-40">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Analysing your resume…</> : <><ScanLine className="h-4 w-4" />Get my free evaluation</>}
      </button>
      {err && <p className="text-xs text-red-400" data-testid="fit-error">{err}</p>}
      <p className="text-center text-[11px] text-zinc-500">Free, one evaluation per person. Handled under our <a href={process.env.REACT_APP_PRIVACY_URL} target="_blank" rel="noreferrer" className="underline hover:text-[#F3C649]">Privacy Policy</a>.</p>
    </div>
  );
};
